import { z } from 'zod';

export const ClubPurchaseOrderStatusSchema = z.enum(['pending', 'paid', 'failed', 'expired']);
export type ClubPurchaseOrderStatus = z.infer<typeof ClubPurchaseOrderStatusSchema>;

export const RobokassaPaymentSessionSchema = z.object({
  provider: z.literal('robokassa'),
  method: z.literal('POST'),
  action: z.string().url(),
  fields: z.record(z.string()),
});
export type RobokassaPaymentSession = z.infer<typeof RobokassaPaymentSessionSchema>;

export const ClubPurchaseOrderSchema = z.object({
  id: z.string(),
  userId: z.string(),
  clubId: z.string(),
  clubTitle: z.string().min(1),
  paywallSlug: z.string().nullable(),
  status: ClubPurchaseOrderStatusSchema,
  amount: z.number().int().positive(),
  currency: z.string().min(1).max(16),
  originalAmount: z.number().int().positive().nullable().optional(),
  discountAmount: z.number().int().min(0).optional(),
  promoCode: z.string().nullable().optional(),
  payerEmail: z.string().email(),
  payerPhone: z.string().min(5).max(64),
  isTest: z.boolean(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  paidAt: z.string().datetime().nullable(),
  entitlementGrantedAt: z.string().datetime().nullable(),
});
export type ClubPurchaseOrder = z.infer<typeof ClubPurchaseOrderSchema>;

export const CreateClubPurchaseOrderBodySchema = z.object({
  payerEmail: z.string().email(),
  payerPhone: z.string().trim().min(5).max(64),
  promoCode: z.string().trim().min(1).max(40).optional(),
});
export type CreateClubPurchaseOrderBody = z.infer<typeof CreateClubPurchaseOrderBodySchema>;

export const ActivateClubPromoCodeBodySchema = z.object({
  code: z.string().trim().min(1).max(40),
});
export type ActivateClubPromoCodeBody = z.infer<typeof ActivateClubPromoCodeBodySchema>;

export const PromoActivationPreviewSchema = z.object({
  clubId: z.string(),
  originalAmount: z.number().int().min(0),
  currency: z.string().min(1).max(16),
  discountAmount: z.number().int().min(0),
  finalAmount: z.number().int().min(0),
  promoCode: z.string(),
  grantsAccessImmediately: z.boolean(),
  entitlementActive: z.boolean().optional(),
});
export type PromoActivationPreview = z.infer<typeof PromoActivationPreviewSchema>;

export const CreateClubPurchaseOrderResponseSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('already_owned'),
    clubId: z.string(),
  }),
  z.object({
    outcome: z.literal('order_ready'),
    order: ClubPurchaseOrderSchema,
    paymentSession: RobokassaPaymentSessionSchema,
    reusedPendingOrder: z.boolean(),
  }),
]);
export type CreateClubPurchaseOrderResponse = z.infer<typeof CreateClubPurchaseOrderResponseSchema>;

export const ClubPurchaseOrderStatusResponseSchema = z.object({
  order: ClubPurchaseOrderSchema,
  entitlementActive: z.boolean(),
  paymentSession: RobokassaPaymentSessionSchema.nullable(),
});
export type ClubPurchaseOrderStatusResponse = z.infer<typeof ClubPurchaseOrderStatusResponseSchema>;
